/**
 * Title and meta description of a venue showcase (`/locale/[slug]`).
 *
 * These used to be built by interpolating the venue fields straight into a
 * template literal. `minifyRestaurantData` returns `null`, not `undefined`, for
 * a venue whose `brief_description` is empty, and a template literal renders
 * that as the four letters "null": on 05/08/2026 the live description of
 * /locale/mana-bistrot read "Lavora con noi! null", and 233 of the 2.980 public
 * showcases had the same empty field.
 *
 * The fallback never has to invent anything: every public showcase has a name,
 * and all but one of those 233 also has a locality.
 */

const BRAND = 'Restworld';

export type VenueMetaSource = {
  restaurantName?: string | null;
  briefDescription?: string | null;
  metaDescription?: string | null;
  locality?: string | null;
};

export type VenueMeta = {
  title: string;
  description: string;
};

export function buildVenueMeta(source?: VenueMetaSource | null): VenueMeta {
  const name = clean(source?.restaurantName);
  const brief = clean(source?.briefDescription);
  const locality = clean(source?.locality);
  // The per-venue SEO override wins when an employer has filled it in. It is a
  // precedence rule, not the fix: only 4 of the 2.980 public showcases carry
  // one, and none of the 233 missing a brief description.
  const seoDescription = clean(source?.metaDescription);

  return {
    title: name ? `${name} | ${BRAND}` : BRAND,
    description: buildDescription({ name, brief, locality, seoDescription }),
  };
}

function buildDescription({
  name,
  brief,
  locality,
  seoDescription,
}: {
  name?: string;
  brief?: string;
  locality?: string;
  seoDescription?: string;
}): string {
  if (seoDescription) return seoDescription;
  if (brief) return `Lavora con noi! ${brief}`;
  // "opportunità di lavoro" and not "posizioni aperte": a showcase stands on its
  // own with no listing at all, and 198 of the 233 venues this fallback serves
  // have none online. `venue-job-positions` simply hides the section.
  if (name && locality) {
    return `Lavora con ${name} a ${locality}: scopri il locale, il team e le opportunità di lavoro su ${BRAND}.`;
  }
  if (name) return `Lavora con ${name}: scopri il locale, il team e le opportunità di lavoro su ${BRAND}.`;
  return `Scopri i locali della ristorazione e le opportunità di lavoro su ${BRAND}.`;
}

// A field made of spaces alone reads as present and renders as absent, so it is
// treated as missing rather than trusted.
function clean(value?: string | null): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}
